// Pure state hook for a segment-in-progress. MapPage is the sole owner of
// map click events (so we can distinguish "clicked a node" vs "clicked empty
// map"), and calls into these helpers. The hook only manages the coords[] +
// broadcasts changes to a caller-supplied onChange.
//
// Lifecycle:
//   seedWithNode(ll)    — begin a new segment anchored at an existing node
//   seedFree(ll)        — begin a new segment at an arbitrary coordinate
//   appendVertex(ll)    — add a mid-route vertex
//   endAtNode(ll)       — finish by anchoring at an existing node
//   finish()            — finish without a trailing node (caller handles save)
//   undo() / clear()    — discard last / all
import { useCallback, useEffect, useRef, useState } from 'react';
export function useSegmentDraw(mapRef, opts) {
    const [coords, setCoords] = useState([]);
    const coordsRef = useRef([]);
    const onChangeRef = useRef(opts.onChange);
    const onFinishRef = useRef(opts.onFinish);
    onChangeRef.current = opts.onChange;
    onFinishRef.current = opts.onFinish;
    const push = useCallback((next) => {
        coordsRef.current = next;
        setCoords(next);
        onChangeRef.current?.(next);
    }, []);
    // Keybindings: Esc clears, Ctrl/Cmd+Z undoes.
    useEffect(() => {
        if (!opts.active)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                push([]);
            }
            else if ((e.key === 'Backspace' || e.key === 'z') && (e.metaKey || e.ctrlKey)) {
                if (coordsRef.current.length)
                    push(coordsRef.current.slice(0, -1));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opts.active, push]);
    // Exiting draw mode cancels any in-progress draft.
    useEffect(() => {
        if (!opts.active && coordsRef.current.length)
            push([]);
    }, [opts.active, push]);
    // Cursor while active.
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        if (opts.active)
            map.getCanvas().style.cursor = 'crosshair';
        return () => { if (map)
            map.getCanvas().style.cursor = ''; };
    }, [opts.active, mapRef]);
    const seedWithNode = useCallback((ll) => push([ll]), [push]);
    const seedFree = useCallback((ll) => push([ll]), [push]);
    const appendVertex = useCallback((ll) => push([...coordsRef.current, ll]), [push]);
    const undo = useCallback(() => coordsRef.current.length && push(coordsRef.current.slice(0, -1)), [push]);
    const clear = useCallback(() => push([]), [push]);
    const finish = useCallback(() => {
        if (coordsRef.current.length >= 2)
            onFinishRef.current?.(coordsRef.current);
        push([]);
    }, [push]);
    const endAtNode = useCallback((ll) => {
        if (coordsRef.current.length === 0)
            return;
        const next = [...coordsRef.current, ll];
        onFinishRef.current?.(next);
        push([]);
    }, [push]);
    return { coords, seedWithNode, seedFree, appendVertex, undo, clear, finish, endAtNode };
}
